import React, { useState } from 'react';
import { Table, Button, Spinner, Form } from 'react-bootstrap';
import { GrView } from "react-icons/gr";
import { CiExport } from "react-icons/ci";
import { AiOutlineDelete } from "react-icons/ai";
import { MdAdd } from "react-icons/md";
import { FiEdit } from 'react-icons/fi';

const CommonTable = ({
    columns,
    data,
    actions,
    onView,
    onEdit,
    onDelete,
    onAdd,
    addButtonText = 'Add New',
    loading = false,
    hasFetched,
    isExport = false,
    handleDownload,
    sortAble = false,
    sortedCol = [],
    isRowClick = false,
    onRowClick = () => { },
    selectable = false,
    selectedIds = [],
    setSelectedIds = () => { },
    idAccessor = "id",
    bulkActions = null,
    filters = null,
    isAdding = false,
    rowHeight = "normal",
}) => {
    const [sortConfig, setSortConfig] = useState({
        key: '',
        direction: 'asc',
    });

    const handleSort = (column) => {
        if (!sortAble || !sortedCol.includes(column)) return;

        let direction = 'asc';
        if (sortConfig.key === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({
            key: column,
            direction,
        });
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return data;

        const sorted = [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortConfig.direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    }, [data, sortConfig]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = data.map((row) => row[idAccessor]);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter((item) => item !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    // Row height configurations
    const rowHeightClasses = {
        compact: 'py-1',
        normal: 'py-2',
        comfortable: 'py-3'
    };

    const rowHeightStyles = {
        compact: { height: '40px' },
        normal: { height: '55px' },
        comfortable: { height: '70px' }
    };

    return (
        <div className="w-full">
            <div
                className={`flex flex-col sm:flex-row justify-end items-start sm:items-center gap-3
  ${filters ? "mb-3 border-b border-gray-300 pb-3" : "mb-3"}`}
            >

                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center !mt-3 gap-2 w-full sm:w-auto">
                    {bulkActions && (
                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            {bulkActions.map((action, index) => (
                                <div key={index} className="w-full sm:w-auto">
                                    {action}
                                </div>
                            ))}
                        </div>
                    )}

                    {data?.length > 0 && isExport && (
                        <button
                            className="flex items-center !py-[10px] !px-[16px] h-[30px] font-bold !rounded-[4px] text-[#000000] justify-center border-1 border-[#E5E5E5] shadow-lg px-3 py-[10px] rounded-[4px] bg-white hover:bg-gray-50 transition w-full sm:w-auto whitespace-nowrap"
                            onClick={handleDownload}
                        >
                            <CiExport size={23} color='black' className=" mr-2" /> Export
                        </button>
                    )}

                    {onAdd && (
                        <button
                            className="flex items-center !py-[10px] !px-[16px] h-[30px] font-bold !rounded-[4px] gap-1 justify-center bg-[#AEF468] text-[#000000]  hover:brightness-95 transition w-full sm:w-auto whitespace-nowrap"
                            onClick={onAdd}
                            disabled={isAdding}
                        >
                            {isAdding ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <MdAdd size={23} color='black' />
                                    {addButtonText}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {filters && (
                <div className="mb-3">
                    {filters}
                </div>
            )}

            <div className="w-full overflow-x-auto">
                <div className="min-w-full inline-block align-middle">
                    <Table className="mb-0">
                        <thead>
                            <tr style={rowHeightStyles[rowHeight]}>
                                {selectable && (
                                    <th className='text-left !bg-[#F6F6F6] !border-none align-middle'>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.length === data.length && data.length > 0}
                                            onChange={handleSelectAll}
                                            className="h-4 w-4 rounded border-gray-300 cursor-pointer !mt-[6px]"
                                            style={{ accentColor: '#AEF468' }}
                                        />
                                    </th>
                                )}
                                {columns.map((col, index) => (
                                    <th
                                        key={index}
                                        onClick={() => handleSort(col.accessor)}
                                        className="align-middle !bg-[#F6F6F6] !border-none"
                                        style={{
                                            cursor: sortAble && sortedCol.includes(col.accessor) ? 'pointer' : 'default',
                                            minWidth: col.minWidth || 'auto',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {col.header}
                                        {sortAble && sortedCol.includes(col.accessor) && sortConfig.key === col.accessor && (
                                            <span className="ms-1">
                                                {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </th>
                                ))}
                                {actions && (
                                    <th className="text-end !bg-[#F6F6F6] !border-none align-middle" style={{ minWidth: '120px' }}>
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)} className="text-center py-5">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : sortedData?.length > 0 ? (
                                sortedData.map((row, rowIndex) => (
                                    <tr
                                        id={`row-${row[idAccessor]}`}
                                        className={isRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
                                        onClick={() => isRowClick && onRowClick(row)}
                                        key={rowIndex}
                                        style={rowHeightStyles[rowHeight]}
                                    >
                                        {selectable && (
                                            <td className={`text-left align-middle ${rowHeightClasses[rowHeight]}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(row[idAccessor])}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectRow(row[idAccessor]);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 rounded border-gray-300 cursor-pointer !mt-[6px]"
                                                    style={{ accentColor: '#AEF468' }}
                                                />
                                            </td>
                                        )}
                                        {columns?.map((col, colIndex) => (
                                            <td key={colIndex} className={`align-middle ${rowHeightClasses[rowHeight]}`}>
                                                {col.accessor === 'id'
                                                    ? row?.serial_number
                                                    : typeof col.accessor === "function"
                                                        ? col.accessor(row)
                                                        : row[col.accessor]}
                                            </td>
                                        ))}
                                        {actions && (
                                            <td className={`text-end align-middle ${rowHeightClasses[rowHeight]}`}>
                                                <div className="d-flex align-items-center justify-content-end gap-2">
                                                    {actions.includes('view') && (
                                                        <button
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onView(row);
                                                            }}
                                                        >
                                                            <GrView size={20} />
                                                        </button>
                                                    )}
                                                    {actions.includes('edit') && (
                                                        <button
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEdit(row);
                                                            }}
                                                        >
                                                            <FiEdit size={20} />
                                                        </button>
                                                    )}
                                                    {actions.includes('delete') && (
                                                        <button
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete(row);
                                                            }}
                                                        >
                                                            <AiOutlineDelete size={21} color="red" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : hasFetched ? (
                                <tr>
                                    <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)} className="text-center py-2">
                                        <div className="text-gray-500">No Data Available</div>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default CommonTable;